import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import {
  api,
  ApiError,
  PendingInvite,
  SessionModeratorRow,
} from "../lib/api";

// Operator (or admin in the operator's chain) → manage moderators for a
// specific session. Same shape as the AdminOperators page but scoped to
// {sid} via the URL. Existing org members can be assigned directly with
// a single API call; external emails get an invite + shareable link.
export default function SessionModerators() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [moderators, setModerators] = useState<SessionModeratorRow[]>([]);
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [copyId, setCopyId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!sessionId) return;
    setErr(null);
    try {
      const [mods, invs] = await Promise.all([
        api.listSessionModerators(sessionId),
        api.listSessionModeratorInvites(sessionId),
      ]);
      setModerators(mods);
      setInvites(invs);
    } catch (e) {
      setErr(
        e instanceof ApiError ? e.message : "could not load moderators",
      );
    }
  }, [sessionId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!sessionId || !inviteEmail.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await api.inviteSessionModerator(sessionId, inviteEmail.trim().toLowerCase());
      setInviteEmail("");
      await refresh();
    } catch (ex) {
      setErr(ex instanceof ApiError ? ex.message : "could not invite");
    } finally {
      setBusy(false);
    }
  }

  async function cancel(inviteId: string) {
    if (!sessionId) return;
    setBusy(true);
    setErr(null);
    try {
      await api.cancelSessionModeratorInvite(sessionId, inviteId);
      await refresh();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "could not cancel invite");
    } finally {
      setBusy(false);
    }
  }

  async function remove(userId: string) {
    if (!sessionId) return;
    if (!confirm("Remove this moderator from the session?")) return;
    setBusy(true);
    setErr(null);
    try {
      await api.removeSessionModerator(sessionId, userId);
      await refresh();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "could not remove moderator");
    } finally {
      setBusy(false);
    }
  }

  async function copyLink(url: string, key: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopyId(key);
      setTimeout(() => setCopyId(null), 1500);
    } catch {
      setErr("Clipboard permission denied — copy the link manually");
    }
  }

  return (
    <main>
      <p style={{ marginBottom: "0.5rem" }}>
        <Link to={`/admin/sessions/${sessionId}`}>← Back to session</Link>
      </p>
      <h1>Moderators</h1>
      <p className="meta">
        Moderators handle approval/rejection and answering for this session
        only. They have no access to other sessions or session settings.
      </p>

      <section className="card" style={{ marginTop: "1rem" }}>
        <h2>Invite moderator</h2>
        <form onSubmit={submit} className="row" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
          <input
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="email@example.com"
            required
            style={{ flex: 1, minWidth: "220px" }}
          />
          <button type="submit" disabled={busy || !inviteEmail.trim()}>
            Send invite
          </button>
        </form>
        {err && (
          <p className="error" role="alert" style={{ marginTop: "0.5rem" }}>
            {err}
          </p>
        )}
      </section>

      {invites.length > 0 && (
        <section style={{ marginTop: "1.5rem" }}>
          <h2>Pending invites ({invites.length})</h2>
          <ul style={{ listStyle: "none", padding: 0 }}>
            {invites.map((inv) => (
              <li
                key={inv.invite_id}
                className="card"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  flexWrap: "wrap",
                }}
              >
                <div style={{ flex: 1, minWidth: "200px" }}>
                  <div style={{ fontWeight: 600 }}>{inv.email}</div>
                  <div className="meta" style={{ fontSize: "0.8rem" }}>
                    Expires {new Date(inv.expires_at).toLocaleString()}
                  </div>
                </div>
                <button
                  type="button"
                  className="secondary"
                  onClick={() => copyLink(inv.invite_url, inv.invite_id)}
                >
                  {copyId === inv.invite_id ? "Copied" : "Copy link"}
                </button>
                <button
                  type="button"
                  className="secondary"
                  onClick={() => cancel(inv.invite_id)}
                  disabled={busy}
                >
                  Cancel
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section style={{ marginTop: "1.5rem" }}>
        <h2>Active moderators ({moderators.length})</h2>
        {moderators.length === 0 ? (
          <p className="meta">
            No moderators yet. Invite someone above to share moderation duties.
          </p>
        ) : (
          <ul style={{ listStyle: "none", padding: 0 }}>
            {moderators.map((m) => {
              const implicit = m.via !== "explicit";
              const viaLabel =
                m.via === "session_owner_admin"
                  ? "Admin — session owner"
                  : m.via === "session_creator_operator"
                    ? "Operator — created this session"
                    : null;
              return (
                <li
                  key={m.user_id}
                  className="card"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    flexWrap: "wrap",
                    opacity: implicit ? 0.85 : 1,
                  }}
                >
                  <div style={{ flex: 1, minWidth: "200px" }}>
                    <div style={{ fontWeight: 600 }}>{m.email}</div>
                    <div className="meta" style={{ fontSize: "0.8rem" }}>
                      {implicit ? (
                        <>{viaLabel} · automatic</>
                      ) : (
                        <>Added {new Date(m.added_at).toLocaleString()}</>
                      )}
                    </div>
                  </div>
                  {implicit ? (
                    <span
                      className="meta"
                      style={{
                        fontSize: "0.75rem",
                        padding: "0.25rem 0.5rem",
                        border: "1px solid var(--border)",
                        borderRadius: "0.375rem",
                      }}
                      title="Inherited from session ownership — manage on the Operators page if needed."
                    >
                      Inherited
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => remove(m.user_id)}
                      disabled={busy}
                      style={{ borderColor: "var(--danger, #dc2626)", color: "var(--danger, #dc2626)" }}
                    >
                      Remove
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </main>
  );
}
